import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Plane } from "lucide-react";
import { useTranslation } from "react-i18next";

const SEO_LINKS = [
  ["/logan-airport-car-rental", "Logan Airport Car Rental"],
  ["/logan-airport-car-rental-pickup-directions", "Logan Airport Pickup Directions"],
  ["/car-rental-locations", "All Locations We Serve"],
  ["/cheap-car-rental-logan-airport", "Cheap Car Rental at Logan"],
  ["/boston-weekly-car-rental", "Weekly Car Rental Boston"],
  ["/weekly-car-rental-logan-airport", "Weekly Car Rental at Logan"],
  ["/economy-car-rental-boston", "Economy Car Rental Boston"],
  ["/long-term-car-rental-boston", "Long-Term Car Rental Boston"],
  ["/contactless-car-rental-boston", "Contactless Car Rental Boston"],
  ["/logan-airport-drop-off", "Logan Airport Pickup & Return"],
  ["/cape-cod-car-rental", "Cape Cod Car Rental"],
  ["/boston-to-cape-cod-car-rental", "Boston to Cape Cod"],
  ["/logan-airport-to-cape-cod-car-rental", "Logan Airport to Cape Cod"],
  ["/boston-to-salem-car-rental", "Boston to Salem"],
  ["/logan-airport-to-salem-car-rental", "Logan Airport to Salem"],
  ["/boston-to-plymouth-car-rental", "Boston to Plymouth"],
  ["/boston-to-foxborough-car-rental", "Boston to Foxborough"],
  ["/logan-airport-to-gillette-stadium-car-rental", "Logan Airport to Gillette Stadium"],
  ["/boston-to-marthas-vineyard-car-rental", "Boston to Martha's Vineyard"],
  ["/boston-to-nantucket-car-rental", "Boston to Nantucket"],
  ["/boston-to-berkshires-car-rental", "Boston to the Berkshires"],
  ["/boston-vacation-car-rental", "Vacation Car Rental Boston"],
];

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer data-testid="footer" className="bg-slate-50 border-t border-slate-100 px-5 sm:px-8 py-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
        <div>
          <Link to="/" aria-label="Belo Rentals home" className="flex items-center w-max">
            <img src={`${process.env.PUBLIC_URL}/belo-wordmark.png`} alt="Belo Rentals" className="h-8 w-auto" />
          </Link>
          <p className="mt-5 text-slate-500 leading-relaxed max-w-xs">
            {t("footer.tagline")}
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">
            <Plane size={15} /> {t("footer.airportBadge")}
          </div>
        </div>

        <div>
          <p className="label-caps text-slate-400 mb-5">{t("footer.navigate")}</p>
          <div className="flex flex-col gap-3">
            <Link to="/fleet" className="text-slate-600 hover:text-blue-600 transition-colors">{t("footer.ourFleet")}</Link>
            <Link to="/car-rental-locations" className="text-slate-600 hover:text-blue-600 transition-colors">Locations We Serve</Link>
            <Link to="/logan-airport-car-rental" className="text-slate-600 hover:text-blue-600 transition-colors">Logan Airport Car Rental</Link>
            <Link to="/logan-airport-drop-off" className="text-slate-600 hover:text-blue-600 transition-colors">Logan Airport Pickup & Return</Link>
            <Link to="/contact" className="text-slate-600 hover:text-blue-600 transition-colors">{t("footer.contact")}</Link>
            <Link to="/admin/login" className="text-slate-600 hover:text-blue-600 transition-colors">{t("footer.admin")}</Link>
          </div>
        </div>

        <div>
          <p className="label-caps text-slate-400 mb-5">{t("footer.reachUs")}</p>
          <div className="flex flex-col gap-4">
            <a href="tel:9782211451" className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors">
              <Phone size={16} /> 978-221-1451
            </a>
            <a href="mailto:Rentals@belorentals.com" className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors">
              <Mail size={16} /> Rentals@belorentals.com
            </a>
            <span className="flex items-start gap-3 text-slate-600">
              <MapPin size={16} className="mt-0.5 shrink-0" />
              <span>235 Washington St, Malden, MA 02148<br />291 Berlin St, Clinton, MA 01510</span>
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-slate-200">
        <p className="label-caps text-slate-400 mb-5">Boston &amp; Logan Airport car rentals</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3" data-testid="footer-seo-links">
          {SEO_LINKS.map(([to, label]) => (
            <Link key={to} to={to} className="text-slate-500 hover:text-blue-600 transition-colors text-sm">{label}</Link>
          ))}
        </div>
        <p className="text-slate-400 text-sm mt-6">
          Serving Boston, Cambridge, Malden, Revere, Logan Airport &amp; 15+ Greater Boston communities.{" "}
          <Link to="/car-rental-locations" className="text-blue-600 hover:underline font-medium">See all locations →</Link>
        </p>
      </div>

      <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-slate-200">
        <p className="label-caps text-slate-400 mb-5">For hosts</p>
        <div className="flex flex-wrap gap-x-6 gap-y-3" data-testid="footer-host-links">
          <Link to="/become-a-host" className="text-slate-500 hover:text-blue-600 transition-colors text-sm">Become a Host</Link>
          <Link to="/host" className="text-slate-500 hover:text-blue-600 transition-colors text-sm">Host Application</Link>
          <Link to="/become-a-host#calculator" className="text-slate-500 hover:text-blue-600 transition-colors text-sm">Host Earnings Calculator</Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-slate-200 text-slate-400 text-sm">
        © {new Date().getFullYear()} Belo Rentals. {t("footer.rights")}
      </div>
    </footer>
  );
}
